import React from "react";
import { DashboardPageLayout } from "../../../components/layout/DashboardPageLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { ssrFormattedDateOr } from "../../../lib/ssrFormattedDate";
import { IncomingOrgInvitationsCard } from "./IncomingOrgInvitationsCard";
import { InviteMemberForm } from "./InviteMemberForm";
import { MemberRoleForm } from "./MemberRoleForm";
import { RemoveOrganizationMemberForm } from "./RemoveOrganizationMemberForm";
import { RevokeOrganizationInviteForm } from "./RevokeOrganizationInviteForm";
import { RoleDescriptionsLegend } from "./RoleDescriptionsLegend";
import type { MainAppPageProps } from "../types";

type OrgMembersPageProps = Pick<
  MainAppPageProps,
  | "csrfToken"
  | "routes"
  | "orgMembers"
  | "orgInvitations"
  | "organization"
  | "incomingOrgInvitations"
  | "membershipRole"
  | "currentUserId"
  | "userDisplay"
>;

function roleLabel(role: string, isCurrentUser: boolean): string {
  const suffix = isCurrentUser ? " (you)" : "";
  return `${role}${suffix}`;
}

export const OrgMembersPage: React.FC<OrgMembersPageProps> = ({
  csrfToken,
  routes,
  orgMembers = [],
  orgInvitations = [],
  incomingOrgInvitations = [],
  organization,
  membershipRole = "member",
  currentUserId,
  userDisplay,
}) => (
  <DashboardPageLayout title="Team">
    <div className="flex w-full flex-col gap-6">
      <IncomingOrgInvitationsCard
        csrfToken={csrfToken}
        routes={routes}
        invitations={incomingOrgInvitations}
        needsDisplayName={Boolean(userDisplay?.needsDisplayName)}
      />
      <Card className="bg-card">
        <CardHeader>
          <CardTitle className="text-base">Invite a teammate</CardTitle>
        </CardHeader>
        <CardContent>
          <InviteMemberForm csrfToken={csrfToken} routes={routes} />
          <RoleDescriptionsLegend
            descriptions={organization?.roleDescriptions}
          />
        </CardContent>
      </Card>

      <Card className="bg-card">
        <CardHeader>
          <CardTitle className="text-base">Members</CardTitle>
        </CardHeader>
        <CardContent>
          {orgMembers.length === 0 ? (
            <p className="text-sm text-muted-foreground">No members yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {orgMembers.map((member) => {
                const isCurrentUser = member.userId === String(currentUserId);
                return (
                  <li
                    key={member.id}
                    className="flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {member.name}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {member.email}
                      </p>
                    </div>
                    <div className="flex flex-col items-start gap-2 sm:items-end">
                      <p className="text-sm text-muted-foreground">
                        <span className="font-medium capitalize text-foreground">
                          {roleLabel(member.role, isCurrentUser)}
                        </span>
                        {member.joinedAtDisplay ? (
                          <span>
                            {" "}
                            · joined{" "}
                            {ssrFormattedDateOr(member.joinedAtDisplay)}
                          </span>
                        ) : null}
                      </p>
                      {!isCurrentUser ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <MemberRoleForm
                            csrfToken={csrfToken}
                            routes={routes}
                            member={member}
                            actorRole={membershipRole ?? "member"}
                          />
                          <RemoveOrganizationMemberForm
                            csrfToken={csrfToken}
                            routes={routes}
                            member={member}
                            actorRole={membershipRole ?? "member"}
                            isCurrentUser={isCurrentUser}
                          />
                        </div>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <Card className="bg-card">
        <CardHeader>
          <CardTitle className="text-base">Outgoing invites</CardTitle>
        </CardHeader>
        <CardContent>
          {orgInvitations.filter((invite) => invite.status === "pending")
            .length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No outgoing invites.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {orgInvitations
                .filter((invite) => invite.status === "pending")
                .map((invite) => (
                  <li
                    key={invite.id}
                    className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
                  >
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {invite.email}
                      </p>
                      <p className="text-sm capitalize text-muted-foreground">
                        {invite.role}
                      </p>
                    </div>
                    <div className="flex flex-col items-start gap-2 sm:items-end">
                      {invite.expiresAtDisplay ? (
                        <p className="text-sm text-muted-foreground">
                          Expires {ssrFormattedDateOr(invite.expiresAtDisplay)}
                        </p>
                      ) : null}
                      <RevokeOrganizationInviteForm
                        csrfToken={csrfToken}
                        routes={routes}
                        invitation={invite}
                      />
                    </div>
                  </li>
                ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  </DashboardPageLayout>
);
